import { evaluatePurchaseGate, serializeProperties, triggerActive } from '@icontactic/logic';

/**
 * <ict-purchase-gate> — single source of truth for the PDP buy state.
 * - Reads the server verdict from its JSON payload (Liquid gate).
 * - Tracks live variant availability (Horizon `variant:update`) and component states (`ict:component-state`).
 * - Disables/enables Horizon's add-to-cart button, explains the reason in text, injects line-item properties
 *   into the product form, blocks submission when invalid, and mirrors the state on the sticky mobile bar.
 * No product handles/IDs are hardcoded: everything comes from the section it lives in.
 */
class IctPurchaseGate extends HTMLElement {
  #components = new Map();
  #options = {};
  #variantAvailable = true;
  #state = { valid: false, reasons: [] };
  #observer = null;
  #io = null;
  #abort = new AbortController();

  connectedCallback() {
    const payload = this.querySelector('script[type="application/json"]');
    this.config = payload ? JSON.parse(payload.textContent || '{}') : {};
    this.#variantAvailable = !!this.config.selected?.available;
    this.#options = this.#optionsFrom(this.config.selected?.options || []);

    const { signal } = this.#abort;
    const root = this.sectionRoot;
    root.addEventListener('ict:component-state', this.#onComponentState, { signal });
    root.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    root.addEventListener('variant:selected', () => this.#apply(), { signal });

    const form = this.form;
    if (form) form.addEventListener('submit', this.#onSubmit, { capture: true, signal });

    this.refs = {
      reason: this.querySelector('[data-ict-reason]'),
      sticky: this.querySelector('[data-ict-sticky]'),
      stickyButton: this.querySelector('[data-ict-sticky-button]'),
      stickyPrice: this.querySelector('[data-ict-sticky-price]'),
    };
    this.refs.stickyButton?.addEventListener('click', this.#onStickyClick, { signal });

    this.#broadcastOptions();
    this.#apply();
    this.#watchButton();
    this.#watchSticky();
    // F3E2-04: components that connected before the gate re-announce their state on this signal.
    root.dispatchEvent(new CustomEvent('ict:gate-ready', { bubbles: true }));
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#io?.disconnect();
  }

  get sectionRoot() {
    return this.closest('[data-ict-scope]') || this.closest('.shopify-section') || this.closest('section') || document.body;
  }

  get form() {
    return this.sectionRoot.querySelector('form[data-type="add-to-cart-form"]');
  }

  get button() {
    return this.form?.querySelector('[ref="addToCartButton"], button[name="add"]') || null;
  }

  #optionsFrom(values) {
    const names = this.config.optionNames || [];
    const out = {};
    names.forEach((n, i) => { if (values[i] !== undefined) out[n] = values[i]; });
    return out;
  }

  #onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    if (resource) {
      this.#variantAvailable = !!resource.available;
      if (Array.isArray(resource.options)) this.#options = this.#optionsFrom(resource.options);
    } else {
      this.#variantAvailable = false;
    }
    this.#broadcastOptions();
    // Horizon re-enables the button after morphing; re-assert the gate after its handlers ran.
    setTimeout(() => this.#apply(), 0);
  };

  #broadcastOptions() {
    this.sectionRoot.dispatchEvent(
      new CustomEvent('ict:options-change', { bubbles: true, detail: { options: this.#options, available: this.#variantAvailable } })
    );
  }

  #onComponentState = (event) => {
    const d = event.detail;
    if (!d || !d.id) return;
    const isNew = !this.#components.has(d.id);
    this.#components.set(d.id, d);
    // F3E2-04: a component seen for the first time may have missed the initial broadcast (module order).
    if (isNew) this.#broadcastOptions();
    this.#apply();
  };

  #apply() {
    const components = [...this.#components.values()].map((c) => ({
      ...c,
      active: c.active ?? triggerActive(c.trigger, this.#options),
    }));
    this.#state = evaluatePurchaseGate({
      purchasable: !!this.config.purchasable,
      reason: this.config.reason,
      variantAvailable: this.#variantAvailable,
      components,
    });
    this.dataset.ictValid = String(this.#state.valid);

    const button = this.button;
    if (button) {
      // Validity already includes variant availability, so the gate is the only authority over `disabled`.
      button.disabled = !this.#state.valid;
      button.dataset.ictDisabled = String(!this.#state.valid);
    }
    this.#syncProperties(components);
    this.#renderReason();
    this.#renderSticky();
    this.dispatchEvent(new CustomEvent('ict:gate-change', { bubbles: true, detail: { ...this.#state } }));
  }

  #syncProperties(components) {
    const form = this.form;
    if (!form) return;
    let holder = form.querySelector('[data-ict-properties]');
    if (!holder) {
      holder = document.createElement('div');
      holder.hidden = true;
      holder.setAttribute('data-ict-properties', '');
      form.appendChild(holder);
    }
    holder.replaceChildren();
    for (const [name, value] of serializeProperties(components)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      holder.appendChild(input);
    }
  }

  #message(reason) {
    if (reason.message) return reason.message;
    const messages = this.config.messages || {};
    return messages[reason.code] || messages.generic || '';
  }

  #renderReason() {
    const el = this.refs?.reason;
    if (!el) return;
    if (this.#state.valid) {
      el.textContent = '';
      el.hidden = true;
      return;
    }
    const first = this.#state.reasons[0];
    el.textContent = first ? this.#message(first) : '';
    el.hidden = !el.textContent;
  }

  #renderSticky() {
    const { sticky, stickyButton, stickyPrice } = this.refs || {};
    if (!sticky) return;
    const price = this.sectionRoot.querySelector('product-price .price');
    if (stickyPrice && price) stickyPrice.textContent = price.textContent.trim();
    if (stickyButton) {
      stickyButton.setAttribute('aria-disabled', String(!this.#state.valid));
      stickyButton.textContent = this.#state.valid ? this.config.labels?.add || 'Add to cart' : this.config.labels?.view || 'See options';
    }
  }

  #onSubmit = (event) => {
    if (this.#state.valid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#focusFirstInvalid();
  };

  #focusFirstInvalid() {
    const invalid = this.#state.reasons.find((r) => r.componentId);
    const target = invalid ? document.getElementById(invalid.componentId) : this.button;
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      const focusable = target.querySelector?.('select, input, button, [tabindex]') || target;
      focusable.focus?.({ preventScroll: true });
    }
    const el = this.refs?.reason;
    if (el && !el.hidden) el.setAttribute('role', 'alert');
  }

  #onStickyClick = (event) => {
    event.preventDefault();
    if (this.#state.valid) {
      this.form?.requestSubmit?.();
    } else {
      this.#focusFirstInvalid();
    }
  };

  #watchButton() {
    const button = this.button;
    if (!button) return;
    this.#observer = new MutationObserver(() => {
      // Horizon toggles `disabled` on variant changes; re-assert the gate verdict.
      if (button.disabled === this.#state.valid) button.disabled = !this.#state.valid;
    });
    this.#observer.observe(button, { attributes: true, attributeFilter: ['disabled'] });
  }

  #watchSticky() {
    const sticky = this.refs?.sticky;
    const button = this.button;
    if (!sticky || !button || !('IntersectionObserver' in window)) return;
    this.#io = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !passed;
        document.body.classList.toggle('ict-sticky-visible', passed);
      },
      { threshold: 0 }
    );
    this.#io.observe(button);
  }
}

if (!customElements.get('ict-purchase-gate')) customElements.define('ict-purchase-gate', IctPurchaseGate);

/* W-166: the Ali Reviews star widget prints "(no reviews yet)" with five empty stars until the first review
   arrives. Empty stars read as a bad rating, so the widget stays hidden while it says so and shows itself the
   moment the text changes. Nothing else is touched: the app keeps rendering into the same block. */
(function () {
  const RE = /no (customer )?reviews yet/i;
  const roots = () => [...document.querySelectorAll('.product-information [id^="shopify-block-"], .product-information .shopify-app-block, .ict-apps')];
  const sweep = () => {
    roots().forEach((el) => {
      if (el.dataset.ictReviewsWatched !== '1' && !/ali|review|rating|star/i.test(el.id + ' ' + el.className + ' ' + el.innerHTML.slice(0, 400))) return;
      el.dataset.ictReviewsWatched = '1';
      const empty = RE.test(el.textContent || '');
      // W-167: the reviews SECTION (.ict-apps) hides as a whole while Ali says there are no reviews, padding included.
      const target = el.classList.contains('ict-apps') ? (el.closest('.shopify-section') || el) : el;
      target.hidden = empty;
      target.classList.toggle('ict-reviews-empty', empty);
    });
  };
  const start = () => { sweep(); const mo = new MutationObserver(sweep); mo.observe(document.body, { childList: true, subtree: true, characterData: true }); setTimeout(() => mo.disconnect(), 15000); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
